import React, { useState, useEffect, useRef } from 'react';
import {
    BuildingLibraryIcon,
    PencilIcon,
    TrashIcon,
    CurrencyDollarIcon,
    EllipsisVerticalIcon,
    XMarkIcon,
    EyeIcon,
    FolderIcon,
    FunnelIcon,
    ChevronDownIcon,
    ArrowDownIcon,
    Bars3Icon,
    Squares2X2Icon,
    ListBulletIcon,
    ClipboardDocumentIcon,
    UserIcon,
    DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { BankDetail } from '../../types/bankDetails';
import { toast } from 'react-hot-toast';
import BankDetailsService from '../../services/bankDetails.service';
import { useVault } from '../../contexts/VaultContext';
import { useAppSelector } from '../../hooks/useRedux';
import ConfirmModal from '../common/ConfirmModal';
import { useCategories } from '../../contexts/CategoryContext';

interface BankDetailsListProps {
    details: BankDetail[];
    onUpdate: () => void;
    onEdit: (detail: BankDetail, isShared?: boolean, sharedKey?: string) => void;
    hideFavoriteIcon?: boolean;
    isAdminView?:boolean
}

const renderCategories = (entryCategories: any[], categories?: any[]) => {
    if (!entryCategories || entryCategories.length === 0) return null;

    const validCategories = entryCategories.filter(entryCategory =>
        categories?.some((userCategory: any) => userCategory.id === entryCategory.id)
    );

    if (validCategories.length === 0) return null;

    return (
        <>
            {validCategories.slice(0, 2).map((category, index) => (
                <React.Fragment key={category.id}>
                    <div className={`inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs
                        ${category.is_primary
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}>
                        <FolderIcon className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate max-w-20"></span>
                    </div>
                </React.Fragment>
            ))}
            {validCategories.length > 2 && (
                <div className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200">
                    +{validCategories.length - 2} more
                </div>
            )}
        </>
    );
};

// Helper function to get stored view mode preference
const getStoredBankViewMode = (): 'grid' | 'list' => {
    try {
        const stored = localStorage.getItem('bankDetailsViewMode');
        if (stored === 'grid' || stored === 'list') {
            return stored;
        }
    } catch (error) {
        console.warn('Failed to read bank view mode preference from localStorage:', error);
    }
    return 'grid'; // Default fallback
};

// Helper function to store view mode preference
const storeBankViewMode = (mode: 'grid' | 'list') => {
    try {
        localStorage.setItem('bankDetailsViewMode', mode);
    } catch (error) {
        console.warn('Failed to save bank view mode preference to localStorage:', error);
    }
};

const BankDetailsList: React.FC<BankDetailsListProps> = ({ details, onUpdate, onEdit, hideFavoriteIcon = false, isAdminView = false }) => {
    const [hoveredWallet, setHoveredWallet] = useState<string | null>(null);
    const [activeWallet, setActiveWallet] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
    const { searchQuery } = useVault();
    const { role_id, team_id } = useAppSelector(state => state.auth);
    const { categories } = useCategories();

    // View mode and filter states - Initialize with stored preference
    const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => getStoredBankViewMode());
    const [selectedBank, setSelectedBank] = useState<BankDetail | null>(null);

    // Enhanced setViewMode that persists the preference
    const handleViewModeChange = (mode: 'grid' | 'list') => {
        setViewMode(mode);
        storeBankViewMode(mode);
    };
    const [sortOption, setSortOption] = useState<'newest' | 'oldest' | 'alphabetical'>('newest');
    const [showSharedOnly, setShowSharedOnly] = useState<boolean>(false);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState<boolean>(false);
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
    const [tempShowSharedOnly, setTempShowSharedOnly] = useState<boolean>(false);
    const sortMenuRef = useRef<HTMLDivElement>(null);
    const filterMenuRef = useRef<HTMLDivElement>(null);

    // Confirm modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [bankToDelete, setBankToDelete] = useState<string>('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    // Detect screen size changes for responsive behavior
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleDeleteClick = (id: string, is_owner: boolean) => {
        setBankToDelete(id);
        setIsOwner(is_owner);
        setShowConfirmModal(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setIsDeleting(true);
            await BankDetailsService.deleteBankDetail(bankToDelete, !isOwner);
            toast.success('Bank account deleted successfully');
            onUpdate();
            setActiveWallet(null);
        } catch (error) {
            toast.error('Failed to delete bank account');
        } finally {
            setIsDeleting(false);
            setShowConfirmModal(false);
        }
    };

    const handleEdit = async (entryId: string, isShared?: boolean, sharedKey?: string) => {
        try {
            if (isAdminView) {
                // Admin view: Use already-decrypted details from props
                const detail = details.find((d) => d.id === entryId);
                if (!detail) {
                    toast.error('Bank Details not found');
                    return;
                }
                // Force read-only mode for admin
                onEdit({ ...detail, permission_level: 'read' }, isShared ?? false, detail.shared_key ?? sharedKey ?? '');
                setActiveWallet(null);
                return;
            }
            const detail = await BankDetailsService.getBankDetailById(entryId, isShared, sharedKey);
            console.log('detail', detail);
            if (!detail.is_owner) {
                const BankDetails = details.filter((entryId) => entryId.id === detail.id);
                detail.is_shared = BankDetails[0].is_shared;
                detail.shared_key = BankDetails[0].shared_key;
                detail.permission_level = BankDetails[0].permission_level;
            }
            onEdit(detail, isShared ?? false, detail.shared_key ?? sharedKey ?? '');
            setActiveWallet(null);
        } catch (err) {
            console.error('Failed to fetch entry:', err);
            toast.error('Failed to fetch entry');
        }
    };

    const handleClone = async (detail: BankDetail) => {
        try {
            if (!isAdminView) {
                toast.error('Clone feature is only available in admin view');
                return;
            }

            const confirmed = window.confirm('Are you sure you want to clone this bank detail to your vault?');
            if (!confirmed) return;

            // Create new bank detail object with all fields
            const newBankDetail: any = {
                name: detail.name,
                bank_name: detail.bank_name,
                account_type: detail.account_type,
                encrypted_routing_number: detail.encrypted_routing_number,
                encrypted_account_number: detail.encrypted_account_number,
                encrypted_swift_code: detail.encrypted_swift_code,
                encrypted_iban_number: detail.encrypted_iban_number,
                encrypted_pin: detail.encrypted_pin,
                encrypted_branch_address: detail.encrypted_branch_address,
                encrypted_branch_phone: detail.encrypted_branch_phone,
                encrypted_notes: detail.encrypted_notes,
                favourite: false,
                workspace_id: team_id || undefined
            };

            // The service will automatically encrypt with the admin's vault key
            await BankDetailsService.createBankDetail(newBankDetail);

            toast.success('Bank detail cloned successfully!');
            onUpdate(); // Refresh the list
        } catch (err) {
            console.error('Failed to clone bank detail:', err);
            toast.error('Failed to clone bank detail');
        }
    };

    // Toggle active wallet on mobile
    const toggleWalletOptions = (id: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent bubbling
        if (isMobile) {
            setActiveWallet(activeWallet === id ? null : id);
        }
    };

    // Close wallet options when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            if (activeWallet) {
                setActiveWallet(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [activeWallet]);

    // Click outside handler for menus
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
                setIsSortMenuOpen(false);
            }

            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
                setIsFilterMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get bank brand colors and backgrounds
    const getBankStyle = (bankName: string) => {
        const bankStyles: Record<string, { bg: string, color: string, logo: string }> = {
            'Chase': {
                bg: 'bg-blue-800',
                color: 'text-white',
                logo: 'linear-gradient(45deg, #117ACA, #00629B)'
            },
            'Bank of America': {
                bg: 'bg-red-700',
                color: 'text-white',
                logo: 'linear-gradient(45deg, #E31837, #CD1309)'
            },
            'Wells Fargo': {
                bg: 'bg-yellow-600',
                color: 'text-white',
                logo: 'linear-gradient(45deg, #D71E28, #FFCD12)'
            },
            'Citibank': {
                bg: 'bg-indigo-600',
                color: 'text-white',
                logo: 'linear-gradient(45deg, #004B93, #0077C8)'
            },
            'Capital One': {
                bg: 'bg-red-600',
                color: 'text-white',
                logo: 'linear-gradient(45deg, #C3002F, #D03027)'
            },
            'default': {
                bg: 'bg-teal-700',
                color: 'text-white',
                logo: 'linear-gradient(45deg, #00796B, #009688)'
            }
        };

        return bankStyles[bankName] || bankStyles['default'];
    };

    const filteredBankDetails = details.filter((detail: BankDetail) => {
        // Apply search filter
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            if (!(
                detail.name?.toLowerCase().includes(searchLower) ||
                detail.bank_name?.toLowerCase().includes(searchLower) ||
                detail.account_type?.toLowerCase().includes(searchLower) ||
                detail.encrypted_account_number?.toLowerCase().includes(searchLower) ||
                detail.encrypted_iban_number?.toLowerCase().includes(searchLower)
            )) {
                return false;
            }
        }

        // Apply shared filter
        if (showSharedOnly && detail.is_owner) {
            return false;
        }

        return true;
    });

    // Sort bank details
    const sortedBankDetails = [...filteredBankDetails].sort((a, b) => {
        switch (sortOption) {
            case 'alphabetical':
                return (a.name || '').localeCompare(b.name || '');
            case 'oldest':
                return new Date(a.updated_at || a.created_at || '').getTime() - new Date(b.updated_at || b.created_at || '').getTime();
            case 'newest':
            default:
                return new Date(b.updated_at || b.created_at || '').getTime() - new Date(a.updated_at || a.created_at || '').getTime();
        }
    });

    // Apply filter settings from temporary state to active state
    const applyFilters = () => {
        setShowSharedOnly(tempShowSharedOnly);
        setIsFilterMenuOpen(false);
    };

    // Reset temporary filter settings
    const resetFilters = () => {
        setShowSharedOnly(false);
        setTempShowSharedOnly(false);
    };

    // Auto-select first bank in list view
    useEffect(() => {
        if (viewMode === 'list' && sortedBankDetails.length > 0 && !selectedBank) {
            setSelectedBank(sortedBankDetails[0]);
        }
    }, [viewMode, sortedBankDetails, selectedBank]);

    // Handle copy to clipboard
    const handleCopyItem = (label: string, value: string) => {
        if (value) {
            navigator.clipboard.writeText(value);
            toast.success(`${label} copied to clipboard`);
        }
    };

    const renderCategoryBadges = (categories: any[], badgeClassName: string, moreClassName: string) => {
        if (!categories || categories.length === 0) return null;

        return (
            <div className="flex items-center space-x-2">
                {categories.slice(0, 2).map(category => (
                    <span key={category.id} className={badgeClassName}>
                        {category.name}
                    </span>
                ))}
                {categories.length > 2 && (
                    <span className={moreClassName}>
                        +{categories.length - 2} more
                    </span>
                )}
            </div>
        );
    };

    // Sort menu component
    const SortMenu = () => {
        return (
            <div className="relative z-50" ref={sortMenuRef}>
                <button
                    onClick={() => {
                        setIsSortMenuOpen(!isSortMenuOpen);
                        setIsFilterMenuOpen(false);
                    }}
                    className="flex items-center px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-sm font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                    <ArrowDownIcon className="h-4 w-4 mr-1" />
                    Sort
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>

                {isSortMenuOpen && (
                    <div className="absolute mt-2 left-0 sm:right-0 sm:left-auto w-48 max-w-[90vw] bg-white shadow-lg rounded-md overflow-hidden z-[9999] border border-gray-200">
                        <div className="py-1">
                            {/* Newest First */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === "newest"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("newest");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Newest First</span>
                                {sortOption === "newest" && <ArrowDownIcon className="h-4 w-4" />}
                            </button>

                            {/* Oldest First */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === "oldest"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("oldest");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Oldest First</span>
                                {sortOption === "oldest" && <ArrowDownIcon className="h-4 w-4" />}
                            </button>

                            {/* Alphabetical */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === "alphabetical"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("alphabetical");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Alphabetical</span>
                                {sortOption === "alphabetical" && (
                                    <ArrowDownIcon className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );

    };

    // Filter menu component
    const FilterMenu = () => {
        return (
            <div className="relative z-50" ref={filterMenuRef}>
                <button
                    onClick={() => {
                        setIsFilterMenuOpen(!isFilterMenuOpen);
                        setIsSortMenuOpen(false);
                        setTempShowSharedOnly(showSharedOnly);
                    }}
                    className="flex items-center px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-sm font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                    <FunnelIcon className="h-4 w-4 mr-1" />
                    Filter
                    {showSharedOnly && (
                        <span className="ml-1 px-1.5 py-0.5 bg-white/30 text-white text-xs rounded-full">
                            1
                        </span>
                    )}
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>

                {isFilterMenuOpen && (
                    <div className="absolute mt-2 right-0 w-64 bg-white shadow-lg rounded-md overflow-hidden z-[9999] border border-gray-200">
                        <div className="flex justify-between items-center p-3 border-b border-gray-200">
                            <span className="font-medium">Filter bank accounts</span>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    resetFilters();
                                }}
                                className="text-sm text-primary-600 hover:text-primary-800"
                            >
                                Reset
                            </button>
                        </div>

                        {/* Filter Options */}
                        <div className="p-3">
                            <div className="space-y-2">
                                <div className="flex items-center">
                                    <input
                                        id="sharedOnly"
                                        type="checkbox"
                                        checked={tempShowSharedOnly}
                                        onChange={(e) => setTempShowSharedOnly(e.target.checked)}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="sharedOnly" className="ml-2 block text-sm text-gray-700">
                                        Shared with me only
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="p-3 border-t border-gray-200">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    applyFilters();
                                }}
                                className="w-full py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header Controls */}
            <div className="sticky top-0 z-40 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 backdrop-blur-sm border-b border-primary-300 dark:border-primary-700 px-4 sm:px-6 py-6 shadow-lg">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-64 h-32 bg-white rounded-full -translate-y-16 translate-x-32"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-24 bg-white rounded-full translate-y-12 -translate-x-24"></div>
                </div>

                <div className="relative flex flex-col gap-6 sm:flex-row sm:flex-wrap sm:items-center lg:justify-between">
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 sm:gap-6 mb-4 sm:mb-0">
                        {/* Title and Icon for Desktop */}
                        <div className="hidden sm:flex items-center space-x-3">
                            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg">
                                <BuildingLibraryIcon className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-white">Bank Details</h1>
                                <p className="text-white/70 text-sm">Manage your bank information</p>
                            </div>
                        </div>

                        {/* Mobile Header */}
                        <div className="sm:hidden">
                            <div className="flex items-center space-x-3 mb-3">
                                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg">
                                    <BuildingLibraryIcon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-white">Bank Details</h1>
                                    <p className="text-white/70 text-xs">Manage your bank information</p>
                                </div>
                            </div>
                        </div>

                        {/* Count Display */}
                        <div className="flex items-center space-x-3 px-3 sm:px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/30 flex-shrink-0 min-w-[160px]">
                            <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
                                <CurrencyDollarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <div>
                                <div className="text-lg sm:text-2xl font-bold text-white">
                                    {sortedBankDetails.length}
                                </div>
                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                    {sortedBankDetails.length === 1 ? 'Account' : 'Accounts'}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto sm:justify-end">
                        {/* Sort Menu */}
                        <div className="flex-1 min-w-[180px] sm:flex-none sm:min-w-0">
                            <SortMenu />
                        </div>

                        {/* Filter Menu */}
                        <div className="flex-1 min-w-[180px] sm:flex-none sm:min-w-0">
                            <FilterMenu />
                        </div>

                        {/* View Toggle */}
                        <div className="hidden sm:flex items-center bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-1 shadow-sm border border-white/30 flex-shrink-0">
                            <button
                                onClick={() => handleViewModeChange('grid')}
                                className={`p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 ${viewMode === 'grid'
                                    ? 'bg-white text-primary-600 shadow-md scale-105'
                                    : 'text-white/80 hover:text-white hover:bg-white/20'
                                    }`}
                                title="Grid View"
                            >
                                <Squares2X2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                            <button
                                onClick={() => handleViewModeChange('list')}
                                className={`p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 ${viewMode === 'list'
                                    ? 'bg-white text-primary-600 shadow-md scale-105'
                                    : 'text-white/80 hover:text-white hover:bg-white/20'
                                    }`}
                                title="List View"
                            >
                                <ListBulletIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {sortedBankDetails.length === 0 && (
                <div className="text-center py-16 px-6">
                    <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                              rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                        <BuildingLibraryIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No bank accounts found</h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                        {searchQuery || showSharedOnly
                            ? "No bank accounts match your search criteria. Try adjusting your search terms or filters."
                            : "Get started by adding your first bank account to securely manage your financial information."
                        }
                    </p>
                </div>
            )}

            {sortedBankDetails.length > 0 && (
                <>
                    {/* Main Content */}
                    <div className="p-6">
                        {viewMode === 'grid' ? (
                            /* Grid View - Mobile Responsive */
                            <div className="bank-details-section grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 p-2 sm:p-4 md:p-6">
                                {sortedBankDetails.map((detail) => {
                                    const bankStyle = getBankStyle(detail.bank_name);

                                    return (
                                        <div
                                            key={detail.id}
                                            className="relative overflow-hidden transition-transform duration-200 hover:scale-105"
                                            onMouseEnter={() => !isMobile && setHoveredWallet(detail.id)}
                                            onMouseLeave={() => !isMobile && setHoveredWallet(null)}
                                            onClick={(e) => isMobile && toggleWalletOptions(detail.id, e)}
                                        >
                                            {/* Wallet front */}
                                            <div className={`h-56 sm:h-64 rounded-xl shadow-xl transition-all duration-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 ${hoveredWallet === detail.id && !isMobile ? 'opacity-0' : 'opacity-100'}`}>
                                                {/* Bank header with logo */}
                                                <div className={`h-16 sm:h-20 ${bankStyle.bg} ${bankStyle.color} p-3 sm:p-4 flex justify-between items-center rounded-t-xl`}>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-lg sm:text-xl font-bold truncate">
                                                            {detail.bank_name}
                                                        </h3>
                                                        <p className="text-xs sm:text-sm opacity-90 truncate">
                                                            {detail.account_type || 'Checking Account'}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center flex-shrink-0">
                                                        {/* Favorite Icon */}
                                                        {detail.favourite && !hideFavoriteIcon && (
                                                            <StarIconSolid className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 mr-1 sm:mr-2" />
                                                        )}

                                                        <div className="rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center mr-1 sm:mr-2"
                                                            style={{ background: bankStyle.logo }}>
                                                            <BuildingLibraryIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                                        </div>

                                                        {/* Mobile menu icon */}
                                                        {isMobile && (
                                                            <button
                                                                onClick={(e) => toggleWalletOptions(detail.id, e)}
                                                                className="text-white p-1 rounded-full hover:bg-white/10 focus:outline-none"
                                                            >
                                                                <EllipsisVerticalIcon className="h-5 w-5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Account balance section */}
                                                <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 dark:border-gray-700">
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">ACCOUNT HOLDER NAME</div>
                                                    <div className="flex items-baseline">
                                                        <div className="text-sm sm:text-base text-gray-800 dark:text-gray-200 truncate">{detail.name}</div>
                                                    </div>
                                                </div>

                                                {/* Account details section */}
                                                <div className="px-3 sm:px-4 py-2 sm:py-3">
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                                        <div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">ACCOUNT NUMBER</div>
                                                            <div className="font-mono text-sm text-gray-800 dark:text-gray-200">
                                                                •••• {detail.encrypted_account_number?.slice(-4) || '****'}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">IBAN NUMBER</div>
                                                            <div className="text-sm text-gray-800 dark:text-gray-200 truncate">•••• {detail.encrypted_iban_number?.slice(-4) || '****'}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Display Categories */}
                                                <div className="absolute bottom-0 left-0 right-0 px-3 sm:px-4 py-2 sm:py-3 flex justify-between items-center">
                                                    <div className="flex items-center space-x-1 sm:space-x-2 flex-1 min-w-0">
                                                        {detail.categories && detail.categories.length > 0 && (
                                                            <div className="flex flex-wrap gap-1">
                                                                {renderCategories(detail.categories, categories)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">
                                                        {new Date(detail.updated_at).toLocaleDateString(undefined, {
                                                            month: 'short',
                                                            day: 'numeric'
                                                        })}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Desktop hover controls */}
                                            {!isMobile && (
                                                <>
                                                    { (isAdminView || (!detail?.is_owner && detail?.permission_level && detail?.permission_level === 'read' && role_id !== 1 && role_id !== 2)) ? (
                                                        <div className={`absolute inset-0 w-full h-full rounded-xl flex flex-col items-center justify-center transition-all duration-300 ${hoveredWallet === detail.id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                                                            style={{ background: bankStyle.logo }}
                                                        >
                                                            <div className="absolute top-0 left-0 right-0 px-4 py-3 text-white">
                                                                <h3 className="text-lg font-semibold">{detail.bank_name}</h3>
                                                                <p className="text-xs opacity-80">Account Management</p>
                                                            </div>

                                                            <button
                                                                onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                                className="w-3/4 py-3 px-4 bg-white text-gray-800 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium mb-3 shadow-md"
                                                            >
                                                                <EyeIcon className="h-5 w-5" />
                                                                <span>View Account</span>
                                                            </button>
                                                            {isAdminView && (
                                                                <button
                                                                    onClick={() => handleClone(detail)}
                                                                    className="w-3/4 py-3 px-4 bg-green-500 text-white hover:bg-green-600 rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium shadow-md"
                                                                >
                                                                    <DocumentDuplicateIcon className="h-5 w-5" />
                                                                    <span>Clone</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className={`absolute inset-0 w-full h-full rounded-xl flex flex-col items-center justify-center transition-all duration-300 ${hoveredWallet === detail.id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                                                            style={{ background: bankStyle.logo }}
                                                        >
                                                            <div className="absolute top-0 left-0 right-0 px-4 py-3 text-white">
                                                                <h3 className="text-lg font-semibold">{detail.bank_name}</h3>
                                                                <p className="text-xs opacity-80">Account Management</p>
                                                            </div>

                                                            <button
                                                                onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                                className="w-3/4 py-3 px-4 bg-white text-gray-800 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium mb-3 shadow-md"
                                                            >
                                                                <PencilIcon className="h-5 w-5" />
                                                                <span>Edit Account</span>
                                                            </button>
                                                            {((detail.is_owner) || (!detail.is_owner)) && (
                                                                <button
                                                                    onClick={() => handleDeleteClick(detail.id, detail.is_owner ?? false)}
                                                                    className="w-3/4 py-3 px-4 bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium shadow-md"
                                                                >
                                                                    <TrashIcon className="h-5 w-5" />
                                                                    <span>Delete Account</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Mobile action menu */}
                                            {isMobile && activeWallet === detail.id && (
                                                <div
                                                    className="absolute right-0 left-0 bottom-0 bg-white dark:bg-gray-800 backdrop-blur-sm rounded-b-lg z-10 p-3 shadow-lg"
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ borderTop: '1px solid rgba(0,0,0,0.1)' }}
                                                >
                                                    <div className="flex justify-between items-center mb-3">
                                                        <h4 className="font-medium text-gray-800 dark:text-white text-sm">Account options</h4>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveWallet(null);
                                                            }}
                                                            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                                        >
                                                            <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                                        </button>
                                                    </div>
                                                    { (isAdminView || (!detail?.is_owner && detail?.permission_level && detail?.permission_level === 'read' && role_id !== 1 && role_id !== 2)) ? (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <button
                                                                onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                                className="py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium"
                                                            >
                                                                <EyeIcon className="h-4 w-4" />
                                                                <span>View Account</span>
                                                            </button>
                                                            {isAdminView && (
                                                                <button
                                                                    onClick={() => handleClone(detail)}
                                                                    className="py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium"
                                                                >
                                                                    <DocumentDuplicateIcon className="h-4 w-4" />
                                                                    <span>Clone</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="grid grid-cols-1 gap-3">
                                                            <button
                                                                onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                                className="py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium"
                                                            >
                                                                <PencilIcon className="h-4 w-4" />
                                                                <span>Edit Account</span>
                                                            </button>
                                                            {((detail.is_owner) || (!detail.is_owner)) && (
                                                                <button
                                                                    onClick={() => handleDeleteClick(detail.id, detail.is_owner ?? false)}
                                                                    className="py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium"
                                                                >
                                                                    <TrashIcon className="h-4 w-4" />
                                                                    <span>Delete Account</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            /* List View with Sidebar */
                            <div className="flex h-[calc(100vh-180px)] bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                {/* Left Panel - Bank List */}
                                <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
                                    <div className="p-4 space-y-3">
                                        {sortedBankDetails.map((detail) => (
                                            <div
                                                key={detail.id}
                                                className="relative group"
                                                onMouseEnter={() => setHoveredWallet(detail.id)}
                                                onMouseLeave={() => setHoveredWallet(null)}
                                            >
                                                <div
                                                    className={`relative p-6 bg-white dark:bg-gray-800 rounded-2xl border-2 transition-all duration-300 cursor-pointer overflow-hidden
                                                ${selectedBank?.id === detail.id
                                                            ? 'border-primary-300 dark:border-primary-600 bg-primary-50/50 dark:bg-primary-900/20 shadow-lg shadow-primary-100 dark:shadow-primary-900/30'
                                                            : 'border-gray-200 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-700 hover:shadow-lg dark:hover:shadow-gray-900/50'
                                                        }`}
                                                    onClick={() => setSelectedBank(detail)}
                                                >
                                                    <div className="flex items-center space-x-5">
                                                        {/* Bank Icon */}
                                                        <div className="w-16 h-10 rounded-xl flex items-center justify-center shadow-lg border border-gray-300 flex-shrink-0 group-hover:shadow-xl transition-shadow duration-300"
                                                            style={{ background: getBankStyle(detail.bank_name).logo }}>
                                                            <BuildingLibraryIcon className="w-8 h-8 text-white" />
                                                        </div>

                                                        {/* Content */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center space-x-3 mb-2">
                                                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                                                    {detail.bank_name}
                                                                </h3>
                                                                {/* Favorite Icon */}
                                                                {detail.favourite && !hideFavoriteIcon && (
                                                                    <StarIconSolid className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                                                                )}
                                                            </div>

                                                            <div className="flex items-center space-x-6">
                                                                <div className="flex items-center space-x-2">
                                                                    <div className="p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                                                        <UserIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                                                    </div>
                                                                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300 truncate max-w-40">
                                                                        {detail.name || "No holder name"}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                                                                        •••• {detail.encrypted_account_number?.slice(-4) || '••••'}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* Categories */}
                                                            {detail.categories && detail.categories.length > 0 && (
                                                                <div className="mt-3">
                                                                    {renderCategories(detail.categories, categories)}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Selection indicator */}
                                                        <div className={`w-1 h-12 rounded-full transition-all duration-300 ${selectedBank?.id === detail.id ? 'bg-primary-500' : 'bg-transparent'
                                                            }`} />
                                                    </div>

                                                    {/* Hover effect overlay */}
                                                    <div className={`absolute inset-0 bg-gradient-to-r from-primary-500/5 to-primary-600/5 rounded-2xl transition-opacity duration-300 pointer-events-none ${hoveredWallet === detail.id ? 'opacity-100' : 'opacity-0'
                                                        }`} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Right Panel - Bank Details */}
                                <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                                    {selectedBank ? (
                                        <div className="h-full p-6 overflow-y-auto">
                                            {/* Header */}
                                            <div className="flex items-center space-x-4 mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
                                                <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg border border-gray-300"
                                                    style={{ background: getBankStyle(selectedBank.bank_name).logo }}>
                                                    <BuildingLibraryIcon className="w-10 h-10 text-white" />
                                                </div>
                                                <div className="flex-1">
                                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                                        {selectedBank.bank_name}
                                                    </h2>
                                                    <p className="text-gray-600 dark:text-gray-400">
                                                        {selectedBank.account_type || 'Checking Account'}
                                                    </p>
                                                </div>
                                            </div>


                                            {/* Details */}
                                            <div className="space-y-6">
                                                {/* Account Number */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Account Number
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <div className="flex-1 flex items-center px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl">
                                                            <BuildingLibraryIcon className="w-5 h-5 text-gray-400 mr-3" />
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                value={selectedBank.encrypted_account_number ? `•••• •••• •••• ${selectedBank.encrypted_account_number.slice(-4)}` : ''}
                                                                className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none font-mono"
                                                            />
                                                        </div>
                                                        <button
                                                            onClick={() => handleCopyItem('Account number', selectedBank.encrypted_account_number || '')}
                                                            className="p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors shadow-lg hover:shadow-xl"
                                                            title="Copy account number"
                                                        >
                                                            <ClipboardDocumentIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* IBAN Number */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        IBAN Number
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <div className="flex-1 flex items-center px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl">
                                                            <UserIcon className="w-5 h-5 text-gray-400 mr-3" />
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                value={selectedBank.encrypted_iban_number || ''}
                                                                className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                                                            />
                                                        </div>
                                                        <button
                                                            onClick={() => handleCopyItem('IBAN number', selectedBank.encrypted_iban_number || '')}
                                                            className="p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors shadow-lg hover:shadow-xl"
                                                            title="Copy IBAN number"
                                                        >
                                                            <ClipboardDocumentIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Categories */}
                                                {selectedBank.categories && selectedBank.categories.length > 0 && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            Vaults
                                                        </label>
                                                        {renderCategories(selectedBank.categories, categories)}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                                                <div className="flex justify-center space-x-4">
                                                    { (isAdminView || (!selectedBank?.is_owner && selectedBank.permission_level === 'read' && role_id !== 1 && role_id !== 2)) ? (
                                                        <button
                                                            onClick={() => handleEdit(selectedBank.id, selectedBank.is_shared, selectedBank.shared_key)}
                                                            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                        >
                                                            <EyeIcon className="h-4 w-4 mr-2" />
                                                            View
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => handleEdit(selectedBank.id, selectedBank.is_shared, selectedBank.shared_key)}
                                                                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                            >
                                                                <PencilIcon className="h-4 w-4 mr-2" />
                                                                Edit
                                                            </button>
                                                            {((selectedBank?.is_owner) || (!selectedBank?.is_owner)) && (
                                                                <button
                                                                    onClick={() => handleDeleteClick(selectedBank.id, selectedBank.is_owner ?? false)}
                                                                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                                >
                                                                    <TrashIcon className="h-4 w-4 mr-2" />
                                                                    Delete
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        // Default state
                                        <div className="h-full flex items-center justify-center p-6">
                                            <div className="text-center">
                                                <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                                                      rounded-2xl flex items-center justify-center mb-6 shadow-lg mx-auto">
                                                    <BuildingLibraryIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                                                </div>
                                                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">Select a Bank Account</h3>
                                                <p className="text-gray-500 dark:text-gray-400">
                                                    Choose a bank account from the list to view details and manage information
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Bank Account"
                message="Are you sure you want to delete this bank account? This action cannot be undone."
                confirmText="Delete"
                isLoading={isDeleting}
            />
        </div>
    );
};

export default BankDetailsList; 